import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import {
    Calendar,
    Clock,
    User,
    MapPin,
    CheckCircle,
    XCircle,
    AlertCircle,
    Briefcase,
    FileText,
} from 'lucide-react'

import { getBusinessByID, approveBusiness } from '../../services'
import { Button, Chip, GridInformation, Typogaphy } from 'vnetwork-ui'
import { useRefetchBusinessQueries, getStatusColor } from '../../types/utils'
import useAuthStore from '@/stores/useAuthStore'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { BusinessDay } from '../../types/response'
import SubHeaders from '@/components/Headers/SubHeaders'
import { paths } from '@/utils/path'
import { formatFromUTC } from '@/utils/function'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import Paper from '@/components/Paper'
import ButtonGroupActions from '@/components/ButtonGroupActions'
import CloseIcons from '@/icons/CloseIcons'
import RelatedBusinesses from './RelatedBusinesses'
import BusinessInfo from './BusinessInfo'
import { IPermissionProps } from '@/types/shared'

// Format business option
const formatBusinessOption = (option: string, t: (key: string) => string) => {
    switch (option) {
        case '0':
            return t('morning')
        case '1':
            return t('afternoon')
        case '2':
            return t('all_day')
        default:
            return '-'
    }
}

// Business Date Card Component
const BusinessDateCard: React.FC<{
    item: BusinessDay
    t: (key: string) => string
}> = ({ item, t }) => {
    const optionColor =
        item.business_option === '2' ? 'bg-blue-100' : 'bg-amber-100'
    const optionTextColor =
        item.business_option === '2' ? 'text-blue-700' : 'text-amber-700'

    return (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div className="p-2 bg-white rounded-md shadow-sm">
                <Calendar className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
                <Typogaphy className="font-medium capitalize">
                    {dayjs(item.business_startdate).format('dddd, DD/MM/YYYY')}
                </Typogaphy>
                <span
                    className={`text-xs px-2 py-0.5 rounded-full ${optionColor} ${optionTextColor}`}
                >
                    {formatBusinessOption(item.business_option, t)}
                </span>
            </div>
        </div>
    )
}

// Approval Status Card with Approve Form
const ApprovalStatusCard: React.FC<{
    title: string
    isApproved: boolean
    approverName?: string
    approvedAt?: string
    feedback?: string
    icon: React.ReactNode
    t: (key: string) => string
    canApprove?: boolean
    isApproving?: boolean
    onApprove?: (feedback: string) => void
    waitingForDependency?: string
    isDisabled?: boolean // When cancelled or rejected
}> = ({
    title,
    isApproved,
    approverName,
    approvedAt,
    feedback,
    icon,
    t,
    canApprove,
    isApproving,
    onApprove,
    waitingForDependency,
    isDisabled,
}) => {
    const [feedbackInput, setFeedbackInput] = useState('Approved')

    const handleApprove = () => {
        if (onApprove) {
            onApprove(feedbackInput)
        }
    }

    // Determine card background
    const getCardStyle = () => {
        if (isDisabled) return 'bg-gray-100 border-gray-300 opacity-60'
        if (isApproved) return 'bg-green-50 border-green-200'
        return 'bg-gray-50 border-gray-200'
    }

    const getStatusColor = () => {
        if (isApproved) return 'success'
        if (isDisabled) return 'default'
        return 'warning'
    }

    return (
        <div className={`p-3 rounded-lg border ${getCardStyle()}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    {icon}
                    <Typogaphy className="font-semibold text-sm">
                        {title}
                    </Typogaphy>
                </div>
                <Chip
                    label={isApproved ? t('approved') : t('pending')}
                    color={getStatusColor()}
                    size="small"
                />
            </div>
            {isApproved && approverName && (
                <div className="flex items-center gap-2 text-sm text-gray-700 mb-1">
                    <User className="w-3.5 h-3.5 text-gray-400" />
                    <span className="font-medium">{approverName}</span>
                </div>
            )}
            {isApproved && approvedAt && (
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                    <Clock className="w-3 h-3 text-gray-400" />
                    <span>{formatFromUTC(approvedAt)}</span>
                </div>
            )}
            {feedback && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                    <Typogaphy size="small" className="text-gray-600 italic">
                        "{feedback}"
                    </Typogaphy>
                </div>
            )}
            {/* Waiting for dependency */}
            {!isApproved && !isDisabled && waitingForDependency && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{waitingForDependency}</span>
                </div>
            )}
            {/* Waiting for approval */}
            {!isApproved &&
                !isDisabled &&
                !canApprove &&
                !waitingForDependency && (
                    <div className="flex items-center gap-2 text-sm text-amber-600">
                        <AlertCircle className="w-3.5 h-3.5" />
                        <span>{t('waiting_for_approval')}</span>
                    </div>
                )}
            {/* Approve Form */}
            {canApprove && !isApproved && !isDisabled && (
                <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                    <textarea
                        value={feedbackInput}
                        onChange={(e) => setFeedbackInput(e.target.value)}
                        placeholder={t('feedback_placeholder')}
                        className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                        rows={2}
                    />
                    <Button
                        size="small"
                        onClick={handleApprove}
                        loading={isApproving}
                        disabled={isApproving}
                        className="w-full"
                    >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        {t('approve')}
                    </Button>
                </div>
            )}
        </div>
    )
}

const Detail: React.FC<IPermissionProps> = () => {
    const { t } = useTranslation('business')
    const { employee } = useAuthStore()
    const { isAdmin } = usePermissionsStore()
    const { businessID } = useParams<{ businessID: string }>()

    const { refetchAll } = useRefetchBusinessQueries()

    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.BUSINESS_DETAIL, businessID],
        queryFn: async () =>
            await getBusinessByID({ business_id: Number(businessID) || 0 }),
    })

    const { mutateAsync: leaderApprove, isPending: isLeaderApproving } =
        useMutationWithHandler(
            { mutationFn: approveBusiness },
            {
                reactQueryKeys: QUERY_KEYS.BUSINESS_DETAIL,
                errorMessage: 'something went wrong',
                showSuccessToast: false,
            }
        )

    const { mutateAsync: bodApprove, isPending: isBodApproving } =
        useMutationWithHandler(
            { mutationFn: approveBusiness },
            {
                reactQueryKeys: QUERY_KEYS.BUSINESS_DETAIL,
                errorMessage: 'something went wrong',
                showSuccessToast: false,
            }
        )

    const businessData = data?.result?.business
    const isOwner = employee?.employee_id === businessData?.employee_id
    const canCancel = businessData?.business_status === 'pending' && isOwner

    // Approval permissions
    const isCancelledOrRejected =
        businessData?.business_status === 'cancelled' ||
        businessData?.business_status === 'rejected'
    const isLeaderApproved = businessData?.business_leader_status === 'approved'
    const isBodApproved = businessData?.business_bod_status === 'approved'
    // If BOD approved but Leader didn't → Leader was skipped
    const isLeaderSkipped = isBodApproved && !isLeaderApproved
    const canLeaderApprove =
        businessData?.business_status === 'pending' &&
        !isLeaderApproved &&
        !isBodApproved
    // Admin can approve BOD even if Leader hasn't approved yet
    const canBodApprove = isAdmin
        ? (businessData?.business_status === 'pending' ||
              businessData?.business_status === 'leader-approved') &&
          !isBodApproved
        : businessData?.business_status === 'leader-approved' && !isBodApproved

    // Grid data for information display
    const gridData = useMemo(() => {
        if (!businessData) return []
        return [
            {
                label: t('business_id'),
                value: `IBN-${businessData.business_id}`,
            },
            {
                label: t('employee_name'),
                value: (
                    <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span>{businessData.employee_name}</span>
                    </div>
                ),
            },
            {
                label: t('department'),
                value: (
                    <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-gray-400" />
                        <span>{businessData.department_name || '-'}</span>
                    </div>
                ),
            },
            {
                label: t('business_created'),
                value: formatFromUTC(businessData.business_created_at),
            },
            {
                label: t('business_status'),
                value: (
                    <Chip
                        label={t(businessData.business_status || '')}
                        color={getStatusColor(
                            businessData.business_status || ''
                        )}
                        size="small"
                    />
                ),
            },
            {
                label: t('business_type'),
                value: (
                    <Chip
                        label={t(businessData.business_type || '')}
                        color="primary"
                        size="small"
                        variant="outlined"
                    />
                ),
            },
            {
                label: t('business_place'),
                value: (
                    <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span>{businessData.business_place || '-'}</span>
                    </div>
                ),
            },
            {
                label: '',
                value: '',
            },
            {
                label: t('business_content'),
                value: (
                    <div className="flex items-start gap-2">
                        <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                        <span className="text-gray-700">
                            {businessData.business_content || '-'}
                        </span>
                    </div>
                ),
                className: 'lg:col-span-4',
            },
        ]
    }, [businessData, t])

    const handleLeaderApprove = async (feedback: string) => {
        if (!businessData?.business_id) return
        try {
            const result = await leaderApprove({
                business_id: businessData.business_id,
                business_feedback: feedback,
            })
            if (!result) return
            toast.success(t('approve_success'))
            refetch()
            refetchAll()
        } catch (error) {
            console.error(error)
        }
    }

    const handleBodApprove = async (feedback: string) => {
        if (!businessData?.business_id) return
        try {
            const result = await bodApprove({
                business_id: businessData.business_id,
                business_feedback: feedback,
            })
            if (!result) return
            toast.success(t('approve_success'))
            refetch()
            refetchAll()
        } catch (error) {
            console.error(error)
        }
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-2">
            {/* Header */}
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.businessAccountList,
                        title: t('business_list'),
                    },
                    {
                        path: '',
                        title: t('business_request_id', {
                            id: businessData?.business_id,
                        }),
                    },
                ]}
            />

            {/* Status Alert */}
            {businessData?.business_status === 'rejected' && (
                <Paper className="p-3 bg-red-50 border border-red-200">
                    <div className="flex items-center gap-2">
                        <XCircle className="w-5 h-5 text-red-500" />
                        <Typogaphy className="font-medium text-red-700">
                            {t('alert_rejected')}
                        </Typogaphy>
                    </div>
                </Paper>
            )}

            {businessData?.business_status === 'cancelled' && (
                <Paper className="p-3 bg-gray-100 border border-gray-200">
                    <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-gray-500" />
                        <Typogaphy className="font-medium text-gray-700">
                            {t('alert_cancelled')}
                        </Typogaphy>
                    </div>
                </Paper>
            )}

            {/* Main Information */}
            <GridInformation
                data={gridData}
                head={{
                    title: t('business_information'),
                }}
            />

            {/* Business Dates */}
            {businessData?.metadata?.days &&
                businessData.metadata.days.length > 0 && (
                    <Paper className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Calendar className="w-5 h-5 text-primary" />
                            <Typogaphy className="font-semibold">
                                {t('business_dates')} (
                                {businessData.metadata.days.length})
                            </Typogaphy>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                            {businessData.metadata.days.map((item) => (
                                <BusinessDateCard
                                    key={`${item.business_startdate}-${item.business_enddate}-${item.business_option}`}
                                    item={item}
                                    t={t}
                                />
                            ))}
                        </div>
                    </Paper>
                )}

            {/* Related Businesses */}
            <RelatedBusinesses
                businessId={Number(businessID)}
                companionCount={data?.result?.companion_count}
                parentBusiness={data?.result?.parent_business}
            />

            {/* Meeting Info (if business type is meeting) */}
            {businessData?.business_type === 'meeting' && (
                <BusinessInfo
                    business={data?.result}
                    disable={
                        businessData?.business_status !== 'pending' ||
                        businessData.employee_id !== employee?.employee_id
                    }
                />
            )}

            {/* Approval Status */}
            <Paper className="p-4">
                <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <Typogaphy className="font-semibold">
                        {t('approval_status')}
                    </Typogaphy>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <ApprovalStatusCard
                        title={t('leader')}
                        isApproved={isLeaderApproved}
                        feedback={businessData?.business_leader_feedback}
                        icon={<User className="w-4 h-4 text-blue-500" />}
                        t={t}
                        canApprove={canLeaderApprove}
                        isApproving={isLeaderApproving}
                        onApprove={handleLeaderApprove}
                        isDisabled={isCancelledOrRejected || isLeaderSkipped}
                    />
                    <ApprovalStatusCard
                        title={t('bom_bod')}
                        isApproved={isBodApproved}
                        feedback={businessData?.business_bod_feedback}
                        icon={<Briefcase className="w-4 h-4 text-purple-500" />}
                        t={t}
                        canApprove={canBodApprove}
                        isApproving={isBodApproving}
                        onApprove={handleBodApprove}
                        waitingForDependency={
                            !isAdmin && !isLeaderApproved
                                ? t('waiting_for_leader')
                                : undefined
                        }
                        isDisabled={isCancelledOrRejected}
                    />
                </div>
            </Paper>

            {/* Action Buttons */}
            <ButtonGroupActions
                actions={[
                    {
                        icon: <CloseIcons />,
                        label: t('cancel_business'),
                        onClick: () => {},
                        variant: 'outlined',
                        disabled: !canCancel,
                    },
                ]}
            />
        </div>
    )
}

const DetailComponent = (props: IPermissionProps) => <Detail {...props} />

export const DetailBusiness = DetailComponent
